/**
 * SaveToDriveModal — put an artifact into the user's Google Drive.
 *
 * Folder browser (remembers the last folder used), a name field, and — for
 * Word / Excel / PowerPoint / Markdown artifacts — a switch to open the copy
 * as a live Google Doc / Sheet / Slides rather than an Office file. Always a
 * NEW file in Drive; nothing there is ever overwritten.
 *
 * Saving needs only Google's `basic` level (drive.file). Browsing the
 * person's own folders needs `read`: without it the dialog saves to My
 * Drive and offers to ask for it, in place, for choosing another folder.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Spin, message as antdMessage } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import { CloudStorageApi } from '@/modules/data-bundles/services/cloudStorageService';
import { useCloudConnections } from '@/modules/data-bundles/hooks/useCloudConnections';
import { GoogleAccessPrompt } from '@/modules/cloud-storage/components/GoogleAccessPrompt';
import { useGoogleReadGate } from '@/modules/cloud-storage/hooks/useGoogleReadGate';
import { driveService, canOpenAsGoogleFormat, type DriveFolder, type SavedDriveFile } from '../../services/driveService';
import { safeHttpUrl } from '@/utils/safeHttpUrl';
import { DriveKindIcon, ExternalLinkIcon, GoogleDriveGlyph } from './driveIcons';
import './DriveFilePicker.css';
import './SaveToDriveModal.css';

interface SaveToDriveModalProps {
  open: boolean;
  artifactId: string;
  title: string;
  format?: string;
  version?: number;
  onClose: () => void;
  onSaved?: (file: SavedDriveFile) => void;
}

interface Crumb {
  id: string | null;
  name: string;
}

export function SaveToDriveModal({ open, artifactId, title, format, version, onClose, onSaved }: SaveToDriveModalProps) {
  const { t } = useLanguage();
  const { loadConnections, connect, getConnection, loading: connectionsLoading } = useCloudConnections();
  const connection = getConnection('google_drive');
  const nativeKind = canOpenAsGoogleFormat(format);
  const gate = useGoogleReadGate(open && !!connection);
  const canBrowse = !!connection && gate.canList;

  const [crumbs, setCrumbs] = useState<Crumb[]>([{ id: null, name: '' }]);
  const [folders, setFolders] = useState<DriveFolder[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [fileName, setFileName] = useState(title);
  const [asNative, setAsNative] = useState(true);
  const [saving, setSaving] = useState(false);
  const defaultApplied = useRef(false);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setCrumbs([{ id: null, name: '' }]);
    setFileName(title);
    setAsNative(true);
    setSaving(false);
    gate.reset();
    defaultApplied.current = false;
    void loadConnections();
  }, [open, title, loadConnections]);

  // Open in the folder the user last exported to, if any.
  useEffect(() => {
    if (!open || !canBrowse || defaultApplied.current) return;
    defaultApplied.current = true;
    CloudStorageApi.getDefaultExportFolder('google_drive')
      .then((saved) => {
        if (saved?.breadcrumbs?.length) {
          setCrumbs(saved.breadcrumbs.map((c, i) => ({ id: i === 0 ? null : c.id, name: c.name })));
        }
      })
      .catch(() => { /* no saved folder — stay on My Drive */ });
  }, [open, connection?.connection_id, canBrowse]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentFolderId = crumbs[crumbs.length - 1]?.id ?? null;
  useEffect(() => {
    if (!open || !canBrowse) return;
    let cancelled = false;
    setLoadingFolders(true);
    driveService.listFolders(currentFolderId || undefined)
      .then((f) => { if (!cancelled) setFolders(f); })
      .catch((err) => {
        if (cancelled) return;
        setFolders([]);
        gate.refusedBy(err);
      })
      .finally(() => { if (!cancelled) setLoadingFolders(false); });
    return () => { cancelled = true; };
  }, [open, currentFolderId, connection?.connection_id, canBrowse]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const save = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      const file = await driveService.saveArtifact({
        artifact_file_id: artifactId,
        version,
        folder_id: currentFolderId || 'root',
        file_name: fileName.trim() || undefined,
        convert_to_native: !!nativeKind && asNative,
      });
      CloudStorageApi.setDefaultExportFolder(
        'google_drive',
        currentFolderId || '',
        crumbs.length > 1 ? crumbs[crumbs.length - 1].name : 'Root',
        crumbs.map((c) => ({ id: c.id, name: c.name })),
      ).catch(() => { /* remembering the folder is best-effort */ });
      const link = safeHttpUrl(file.web_view_link);
      antdMessage.success({
        content: (
          <span>
            {t('chat.drive.saved', { name: file.name })}
            {link && (
              <>
                {' '}
                <a href={link} target="_blank" rel="noopener noreferrer">
                  {t('chat.drive.openInDrive')}
                </a>
              </>
            )}
          </span>
        ),
        duration: 6,
      });
      onSaved?.(file);
      onClose();
    } catch (err: any) {
      const code = err?.response?.data?.error_code;
      antdMessage.error(
        code === 'CLOUD_NOT_CONNECTED'
          ? t('common.upload.errors.CLOUD_NOT_CONNECTED')
          : err?.response?.data?.error || t('chat.drive.saveFailed'),
      );
    } finally {
      setSaving(false);
    }
  }, [saving, artifactId, version, currentFolderId, fileName, nativeKind, asNative, crumbs, onSaved, onClose, t]);

  if (!open) return null;

  const notConnected = !connectionsLoading && !connection;
  const folderLabel = crumbs.length > 1 ? crumbs[crumbs.length - 1].name : t('chat.drive.myDrive');

  return createPortal(
    <div className="drive-picker__overlay" onMouseDown={onClose} role="presentation">
      <div
        className="drive-picker save-to-drive"
        role="dialog"
        aria-modal="true"
        aria-label={t('chat.drive.saveToDrive')}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="drive-picker__header">
          <span className="drive-picker__logo"><GoogleDriveGlyph size={20} /></span>
          <div className="drive-picker__heading">
            <h2 className="drive-picker__title">{t('chat.drive.saveToDrive')}</h2>
            {connection && <span className="drive-picker__account">{connection.account_email}</span>}
          </div>
          <button type="button" className="drive-picker__close" onClick={onClose} aria-label={t('common.close')}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </header>

        {notConnected ? (
          <div className="drive-picker__connect">
            <GoogleDriveGlyph size={40} />
            <h3>{t('chat.drive.notConnectedTitle')}</h3>
            <p>{t('chat.drive.notConnectedSaveBody')}</p>
            <button type="button" className="drive-picker__btn drive-picker__btn--primary" onClick={() => void connect('google_drive')}>
              {t('chat.drive.connect')}
            </button>
          </div>
        ) : (
          <>
            <div className="save-to-drive__fields">
              <label className="save-to-drive__field">
                <span className="save-to-drive__label">{t('chat.drive.fileName')}</span>
                <input
                  ref={nameRef}
                  type="text"
                  dir="auto"
                  value={fileName}
                  onChange={(e) => setFileName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void save(); } }}
                  spellCheck={false}
                />
              </label>
              {nativeKind && (
                <label className="save-to-drive__switch">
                  <input type="checkbox" checked={asNative} onChange={(e) => setAsNative(e.target.checked)} />
                  <span className="save-to-drive__switch-track" aria-hidden="true"><span className="save-to-drive__switch-thumb" /></span>
                  <span className="save-to-drive__switch-text">
                    <span>{t(`chat.drive.openAs.${nativeKind}`)}</span>
                    <span className="save-to-drive__switch-hint">{t('chat.drive.openAsHint')}</span>
                  </span>
                </label>
              )}
            </div>

            <div className="drive-picker__tabs">
              <span className="save-to-drive__folder-label">{t('chat.drive.saveIn')}</span>
              <nav className="drive-picker__crumbs save-to-drive__crumbs" aria-label={t('chat.drive.saveIn')}>
                {crumbs.map((c, i) => (
                  <React.Fragment key={`${c.id ?? 'root'}-${i}`}>
                    {i > 0 && <span className="drive-picker__crumb-sep" aria-hidden="true">/</span>}
                    <button
                      type="button"
                      className={`drive-picker__crumb ${i === crumbs.length - 1 ? 'is-current' : ''}`}
                      onClick={() => setCrumbs((prev) => prev.slice(0, i + 1))}
                    >
                      {i === 0 ? t('chat.drive.myDrive') : c.name}
                    </button>
                  </React.Fragment>
                ))}
              </nav>
            </div>

            <div className="drive-picker__list save-to-drive__list">
              {gate.pending || loadingFolders ? (
                <div className="drive-picker__state"><Spin /></div>
              ) : !canBrowse ? (
                <GoogleAccessPrompt
                  access="read"
                  reason="folders"
                  variant="card"
                  onGranted={gate.reset}
                />
              ) : folders.length === 0 ? (
                <div className="drive-picker__state">{t('chat.drive.noSubfolders')}</div>
              ) : (
                folders.map((folder) => (
                  <button
                    type="button"
                    key={folder.folder_id}
                    className="drive-picker__row drive-picker__row--folder"
                    onClick={() => setCrumbs((prev) => [...prev, { id: folder.folder_id, name: folder.name }])}
                  >
                    <span className="drive-picker__row-icon"><DriveKindIcon kind="folder" /></span>
                    <span className="drive-picker__row-name" dir="auto">{folder.name}</span>
                    <svg className="drive-picker__row-chevron" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
                  </button>
                ))
              )}
            </div>

            <footer className="drive-picker__footer">
              <span className="drive-picker__count">{t('chat.drive.newFileNote')}</span>
              <div className="drive-picker__footer-actions">
                <button type="button" className="drive-picker__btn" onClick={onClose}>{t('common.cancel')}</button>
                <button type="button" className="drive-picker__btn drive-picker__btn--primary" disabled={saving} onClick={() => void save()}>
                  {saving ? t('chat.drive.saving') : t('chat.drive.saveHere', { folder: folderLabel })}
                  {!saving && <span className="save-to-drive__btn-icon"><ExternalLinkIcon size={13} /></span>}
                </button>
              </div>
            </footer>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
